<script lang="ts" setup>
import { computed, ref, toRef } from 'vue'
import type { FormInstance, FormValidateCallback } from 'element-plus'
import { useContainerBooking } from '~/compositions/useContainerBooking'
import type { AppointmentSlot } from '~/models/appointmentSlot'
import { formatDT } from '~/utils'
import { getTerminalLabel } from '~/constants'
import type { Container } from '~/models/containers'
import type { GETAutoBookRequest } from '~/services/apiClient'
import LicensePlateSelector from '~/components/inputs/LicensePlateSelector.vue'
import ChassisTypeSelector from '~/components/inputs/ChassisTypeSelector.vue'

const props = defineProps<{
  container: Container
  appointmentSlot?: AppointmentSlot
  inline?: boolean
  existingAutoBookRequest?: GETAutoBookRequest
}>()

const existingSettingsRef = toRef(props, 'existingAutoBookRequest')

const {
  form,
  rules,
  ownChassis,
  statusColor,
  requiresOwnChassisSelector,
  usesLicensePlate,
  licensePlateShouldBeDisabled,
  shouldPrefillLicensePlate,
  requiresMasterBL,
  getValues: getBookingValues,
} = useContainerBooking({
  container: props.container,
  appointmentSlot: props.appointmentSlot,
  existingSettings: existingSettingsRef,
})

const formRef = ref<FormInstance>()

function validate(callback?: FormValidateCallback) {
  if (!formRef.value) return false
  return formRef.value.validate(callback)
}

function getValues() {
  return getBookingValues()
}

const terminal = computed(() => props.container.terminal)
const autoBookStatus = computed(() => props.container.auto_book_request_status)

defineExpose({ getValues, validate })
</script>

<template>
  <el-form
    ref="formRef"
    :model="form"
    :rules="rules"
    :inline="props.inline"
    size="small"
    class="booking-dialog-form"
  >
    <el-form-item label="Container #:">
      {{ props.container.number }}
    </el-form-item>
    <el-form-item label="Terminal:" class="min-w-100px">
      <template v-if="terminal">
        {{ getTerminalLabel(terminal) }}
      </template>
      <template v-else>Unknown</template>
    </el-form-item>
    <el-form-item v-if="props.appointmentSlot" label="Time:">
      {{ formatDT(props.appointmentSlot.window_start) }}
      <template v-if="props.appointmentSlot.window_end">
        - {{ formatDT(props.appointmentSlot.window_end) }}
      </template>
    </el-form-item>
    <el-form-item
      v-if="requiresMasterBL"
      label="Master BL #:"
      prop="masterBL"
      :class="{ 'max-w-200px': inline }"
    >
      <el-input v-model="form.masterBL" placeholder="Enter Here">
        <template #prefix>
          <i-la:scroll />
        </template>
      </el-input>
    </el-form-item>
    <el-form-item
      v-if="usesLicensePlate && terminal"
      data-testid="license-plate-form-item"
      label="License plate #:"
      prop="licensePlate"
      :class="{ 'max-w-380px': inline }"
    >
      <LicensePlateSelector
        v-if="!props.container.booked_appointment && terminal"
        v-model="form.licensePlate"
        :terminal="terminal"
        :disabled="licensePlateShouldBeDisabled"
        :auto-select-value="shouldPrefillLicensePlate"
        :class="{ 'w-full': !inline }"
        :show-icon="!inline"
      />
      <span v-else>{{ form.licensePlate }}</span>
    </el-form-item>
    <el-form-item
      v-if="requiresOwnChassisSelector"
      label="Chassis:"
      prop="ownChassis"
      :class="{ 'max-w-380px': inline }"
    >
      <ChassisTypeSelector
        v-model="ownChassis"
        :disabled="Boolean(props.container.booked_appointment)"
      />
    </el-form-item>
    <el-form-item v-if="autoBookStatus" label="Auto-book Status:">
      <span :style="{ color: statusColor }">
        {{ autoBookStatus }}
      </span>
    </el-form-item>
  </el-form>
</template>

<style lang="scss">
.booking-dialog-form {
  margin-bottom: 8px;
  .el-form-item__label {
    font-weight: bold;
  }
  .el-form-item {
    margin-right: 15px;
  }
  .el-form-item--small {
    // height:  42px;
    margin-bottom: 0px;
  }
}
</style>
